import React, { useEffect, useState } from 'react';
import { Grid, Typography, Stack } from '@mui/material';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import SectionCard from './SectionCard';

const PLATFORM_COLORS = { ANDROID: '#3ddc84', IOS: '#8e8e93' };

const StatRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value ?? '—'}
    </Typography>
  </Stack>
);

/** fetchDevices() -> { total, active, inactive, platformDistribution, newRegistrations, deactivations, subscribersWithMultipleDevices, registrationTrend } */
const DeviceAnalyticsSection = ({ fetchDevices, reloadKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchDevices()
      .then((res) => setData(res.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const trend = (data?.registrationTrend || []).map((p) => ({
    label: new Date(p.date).toLocaleDateString([], { month: 'short', day: 'numeric' }),
    count: p.count,
  }));

  // Only Android and iOS are supported platforms — drop anything else the API still returns.
  const platformData = (data?.platformDistribution || []).filter((p) => p.platform === 'ANDROID' || p.platform === 'IOS');

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <SectionCard title="Device Overview" loading={loading} error={error} isEmpty={!loading && !error && !data}>
          <StatRow label="Total Devices" value={data?.total?.toLocaleString()} />
          <StatRow label="Active Devices" value={data?.active?.toLocaleString()} />
          <StatRow label="Inactive Devices" value={data?.inactive?.toLocaleString()} />
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <SectionCard
          title="Platform Distribution"
          loading={loading}
          error={error}
          isEmpty={!loading && !error && platformData.length === 0}
        >
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={platformData} dataKey="count" nameKey="platform" outerRadius={80}>
                {platformData.map((p) => (
                  <Cell key={p.platform} fill={PLATFORM_COLORS[p.platform] || '#9e9e9e'} />
                ))}
              </Pie>
              <Legend />
              <Tooltip formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, props.payload.platform]} />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <SectionCard
          title="Registration Trend"
          loading={loading}
          error={error}
          isEmpty={!loading && !error && trend.length === 0}
        >
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#1565c0" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

export default DeviceAnalyticsSection;
